import { useState, useMemo, useRef } from "react";
import { BULK_AD_STATE_ENABLED, BULK_AD_STATE_PAUSED, GOALS } from "components/core/blocks/AdPlan/constants";
import Loading from "components/core/blocks/Loading";
import BulkActionsTable from "views/merchandise/tables/BulkActionsTable";
import { useAcosOrRoas } from "utils/acosOrRoas";
import AdvertisementExpander from "./advertising/AdvertisementExpander";
import moment from "moment";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "utils/api/index";
import BulkAdPlanActionForm from "./advertising/BulkAdPlanActionForm";
import { successToast, errorToast } from "utils/toasts";
import { CategoryField } from "components/core/blocks/AdPlan/fields";
import { formatCurrency } from "utils/formatNumber";
import { useAppSelector } from "redux/hooks";
import { acosToRoas, getWalmartBudgetMinimum } from "components/core/blocks/AdPlan/utils";
import useCustomTableFilters from "hooks/useCustomTableFilters";
import {
  AD_STATUS_FILTER,
  AD_SOURCE_FILTER,
  NTM_STATUS_FILTER,
  SCHEDULED_BUDGET_CHANGES_FILTER,
  buildBiddingProgramFilter,
  AD_NAME_FILTER,
  SERVING_STATUS_FILTER,
  servingStatusOptions,
} from "./advertising/adLifecycleFilters";
import { buildDateColumn } from "./merchandise/columns/buildDateColumn";
import { formatScheduledAction } from "./advertising/ScheduledBudgetSummary";
import { getAdTypeName } from "utils/getAdTypeName";
import { buildCustomTagsFilter } from "./advertising/productLifecycleFilters";
import BulkTagsForm from "components/core/blocks/BulkTagsForm";
import { useModuleEnabled } from "redux/selectors/modules";
import BulkAdPlanRulesForm from "./advertising/BulkAdPlanRulesForm";
import { Flex } from "../../styled-system/jsx";
import { groupAdPlans } from "./advertising/utils";
import { ButtonGroup } from "components/core/basic/ButtonGroup";
import { BulkDayPartingChart } from "./advertising/DayPartingChart";
import { AdvertisementCell } from "../modules/advertising/tableCells/AdvertisementCell";
import { useBulkUpdateAdStateV2 } from "../modules/advertising/api/useBulkUpdateAdStateV2";
import BulkActionFeedbackModal from "./advertising/BulkActionFeedbackModal";
import { useBulkUpdateAdState } from "modules/advertising/api/useBulkUpdateAdState";
import { css } from "../../styled-system/css";
import ExpanderContainer from "components/core/basic/ExpanderContainer";
import { useBulkUpdateAdPropertiesV2 } from "modules/advertising/api/useBulkUpdateAdPropertiesV2";

const updateValues = (initialValue, changeValue, changeUnit, changeDirection, targetUnits) => {
  initialValue = parseFloat(initialValue);
  changeValue = parseFloat(changeValue);
  if (changeDirection === "increase") {
    // Handle case where percent added to percent metric (i.e. acos goals)
    if (changeUnit === "currency" || changeUnit === targetUnits) {
      return initialValue + changeValue;
    }
    if (changeUnit === "percent") {
      return initialValue * (1 + changeValue / 100);
    }
  }

  if (changeDirection === "decrease") {
    // Handle case where percent added to percent metric (i.e. acos goals)
    if (changeUnit === "currency" || changeUnit === targetUnits) {
      return initialValue - changeValue;
    }

    if (changeUnit === "percent") {
      return initialValue * (1 - changeValue / 100);
    }
  }

  if (changeDirection === "specify") {
    return changeValue;
  }
};

const AdvertisementsTable = ({
  stats: adPlans,
  marketPlace,
  channel,
  smallViewportWidthTable,
  categoryDetails = {},
  categories,
  isLoading,
  filteredByAdType = false,
  showTabs = false,
  leftActionButton,
  defaultPageSize = 10,
}) => {
  const [selectedAds, setSelectedAds] = useState([]);
  const [tab, setTab] = useState("all");
  const [destinationCategoryId, setDestinationCategoryId] = useState(null);
  const [bulkActionFormData, setBulkActionFormData] = useState({
    actionUnit: "percent",
  });
  const [filledTags, setFilledTags] = useState([]);
  const [dayPartingData, setDayPartingData] = useState({
    dayPartingMethod: 0,
  });
  const [showBulkActionFeedbackModal, setShowBulkActionFeedbackModal] = useState(false);
  const [bulkActionFeedback, setBulkActionFeedback] = useState();

  const [stats, adGroups] = useMemo(() => {
    if (!adPlans) {
      return [[], {}];
    }
    const adGroups = groupAdPlans(adPlans);
    return [adGroups[tab] ?? [], adGroups];
  }, [adPlans, tab]);

  const TABLE_ID = filteredByAdType ? "preFilteredAdvertisementsTable" : "advertisementsTable";
  const tableRef = useRef();
  const queryClient = useQueryClient();
  const user = useAppSelector((state) => state.user);
  const acosOrRoas = useAcosOrRoas();

  const hasNTMModuleEnabled = useModuleEnabled("NON_TRELLIS_MANAGED_ADS");
  const hasBulkActionsModuleEnabled = useModuleEnabled("AD_BULK_ACTIONS");

  const resolvedTableData = useMemo(
    () => {
      return tableRef.current?.getResolvedState()?.sortedData;
    },
    // Below intentional to keep resolved table data up-to-date when higher level filters applied
    // With deps, react-table only resolves state for internal filters
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tableRef.current],
  );

  const updateDayParting = useMutation({
    mutationFn: async (body) => {
      return await api.post("/api/bulk_update_dayparting/", body);
    },

    onSuccess: () => {
      successToast({
        message: "Dayparting Successfully Updated",
      });
      queryClient.invalidateQueries();
    },

    onError: (error) => {
      errorToast({
        message: "Failed to Update Dayparting",
        error,
      });
    },
  });

  const updateHandler = useMutation({
    mutationFn: async (body) => {
      const reqData = body.data ?? {};
      const skip = body.skip ?? false;
      if (skip) {
        return true;
      }
      return await api.put("/api/gvads/advproduct/", reqData);
    },

    onSuccess: (data, variables) => {
      if (!variables.isLast) {
        return;
      }
      successToast({
        message: `Updating Media Plan${
          (
            selectedAds?.length > 1 ||
            resolvedTableData?.map((row) => row?._original)?.filter((d) => d?.updated)?.length > 1
          ) ?
            "s"
          : ""
        }`,
      });

      setSelectedAds([]);
      setBulkActionFormData({ actionUnit: "percent" });
      queryClient.invalidateQueries();
      return data;
    },

    onError: (error, variables) => {
      errorToast({
        message: `Failed to Update Media Plan ${variables.data.name}`,
        error,
      });
    },
  });

  const updateStatusHandler = useMutation({
    mutationFn: async (body) => {
      const { state, id } = body.data ?? {};

      const reqData = { id, state };
      const skip = body.skip ?? false;
      if (skip) {
        return true;
      }
      return await api.post("/api/set_campaign_state/", reqData);
    },

    onSuccess: (data, variables) => {
      if (!variables.isLast) {
        return;
      }
      successToast({
        message: `Updating Media Plan${
          (
            selectedAds?.length > 1 ||
            resolvedTableData?.map((row) => row?._original)?.filter((d) => d?.updated)?.length > 1
          ) ?
            "s"
          : ""
        }`,
      });

      setSelectedAds([]);
      setBulkActionFormData({ actionUnit: "percent" });
      queryClient.invalidateQueries();
      return data;
    },

    onError: (error, variables) => {
      errorToast({
        message: `Failed to Update Media Plan ${variables.data.name}`,
        error,
      });

      if (variables.isLast) {
        setSelectedAds([]);
        setBulkActionFormData({ actionUnit: "percent" });
        queryClient.invalidateQueries();
      }
    },
  });

  const updateStateMutation = useBulkUpdateAdState();
  const bulkUpdatePropertiesMutation = useBulkUpdateAdPropertiesV2();

  const updatePlanState = (state, selected) => {
    const ads = selected.map((id) => {
      const ad = stats?.find((ad) => ad.ad_id.toString() === id);
      return {
        id: parseInt(id),
        name: ad?.ad_name ?? "",
      };
    });

    updateStateMutation.mutate(
      { ads, state },
      {
        onSettled: () => {
          setSelectedAds([]);
          setBulkActionFormData({ actionUnit: "percent" });
        },
      },
    );
  };

  const bulkUpdateStatusHandler = useBulkUpdateAdStateV2();

  const bulkUpdatePlanState = (state) => {
    let newState;

    if (state === "paused") {
      newState = BULK_AD_STATE_PAUSED;
    }
    if (state === "active") {
      newState = BULK_AD_STATE_ENABLED;
    }

    const updateData = {
      ad_ids: selectedAds,
      state: newState,
      marketplace: marketPlace.marketPlace,
    };

    bulkUpdateStatusHandler.mutate(updateData, {
      onSettled: (data, error, variables) => {
        const feedbackDetails = { resultMessages: [] };

        const successfulStatusUpdates =
          Object.keys(data)
            .map((adType) => Object.keys(data[adType]["success"]))
            .flat() ?? [];
        const failedStatusUpdates =
          Object.keys(data)
            .map((adType) => Object.keys(data[adType]["error"]))
            .flat() ?? [];

        const { state } = variables;

        if (successfulStatusUpdates.length) {
          feedbackDetails.resultMessages.push(
            `${successfulStatusUpdates.length} media plan${successfulStatusUpdates.length === 1 ? "" : "s"} ${state.toLowerCase()} successfully.`,
          );
        }
        if (failedStatusUpdates.length) {
          feedbackDetails.resultMessages.push(
            `${failedStatusUpdates.length} media plan${failedStatusUpdates.length === 1 ? "" : "s"} failed to ${state.toLowerCase() === "paused" ? "pause" : "enable"}.`,
          );

          const failedMediaPlanDetails = adPlans.filter((ad) => failedStatusUpdates.includes(ad.id.toString()));
          feedbackDetails.failedEntities = failedMediaPlanDetails;
          feedbackDetails.entityType = "advertisement";
        }

        setShowBulkActionFeedbackModal(true);
        setBulkActionFeedback(feedbackDetails);

        setSelectedAds([]);
        setBulkActionFormData({ actionUnit: "percent" });
      },
    });
  };

  const scheduleHandler = useMutation({
    mutationFn: async (body) => {
      const reqData = body.data ?? {};
      if (body.throwError) {
        throw new Error(body.throwError);
      }
      return await api.post("/api/scheduledactions/", reqData);
    },

    onSuccess: (data, variables) => {
      if (!variables.isLast) {
        return;
      }
      successToast({
        message: `Successfully Scheduled Budget Update${selectedAds?.length > 1 ? "s" : ""}`,
      });

      setSelectedAds([]);
      setBulkActionFormData({ actionUnit: "percent" });
      queryClient.invalidateQueries();
    },

    onError: (error, variables) => {
      errorToast({
        message:
          variables.throwError ?
            variables.throwError
          : `Failed to Schedule Budget Update${selectedAds?.length > 1 ? "s" : ""}`,

        error,
      });
      setSelectedAds([]);
      queryClient.invalidateQueries();
    },
  });

  const scheduleBudgetUpdates = async () => {
    for (let i = 0; i < selectedAds?.length; i++) {
      const { actionType, actionValue, actionUnit, startDate, endDate } = bulkActionFormData;

      const adToUpdate = stats.find((ad) => ad.id === selectedAds[i]);

      // Check if scheduled action already exists that overlaps, if so return early and tell user
      const overlappingAction = adToUpdate?.scheduled_actions.some((sa) => {
        if (
          moment(startDate).isBetween(
            sa.scheduled_action_start_date.slice(0, 10),
            sa.scheduled_action_end_date.slice(0, 10),
            undefined,
            "[]",
          ) ||
          moment(endDate).isBetween(
            sa.scheduled_action_start_date.slice(0, 10),
            sa.scheduled_action_end_date.slice(0, 10),
            undefined,
            "[]",
          )
        ) {
          return true;
        }
        return false;
      });

      if (overlappingAction) {
        if (i === selectedAds?.length - 1) {
          // Make sure updates shown for all successfully added actions as tags, even if overlap prevents last action from being created
          scheduleHandler.mutate({
            data: null,
            isLast: true,
            throwError: `Failed to create scheduled update for ${adToUpdate?.ad_name}. The selected date range collides with a previously scheduled action.`,
          });
        } else {
          errorToast({
            message: `Failed to create scheduled update for ${adToUpdate?.ad_name}. The selected date range collides with a previously scheduled action.`,
          });
        }
        continue;
      }

      const updateData = formatScheduledAction(
        selectedAds[i],
        startDate,
        endDate,
        actionUnit,
        actionValue,
        actionType,
        channel,
      );

      scheduleHandler.mutate({
        data: updateData,
        isLast: i === selectedAds?.length - 1,
      });
    }
    setSelectedAds([]);
  };

  const updateTags = useMutation({
    mutationFn: async (updateData) => {
      const { ad_id, tags } = updateData;
      return await api.patch(`/ads/${ad_id}/`, { tags });
    },

    onSuccess: (res, variables) => {
      if (!variables.isLast) {
        return;
      }
      successToast({
        message: `Media Plan Tags Successfully Updated`,
      });

      setSelectedAds([]);
      setFilledTags([]);
      queryClient.invalidateQueries();
    },

    onError: (error, variables) => {
      if (!variables.isLast) {
        return;
      }
      errorToast({
        message: `Failed to Update Media Plan Tags`,
        error,
      });
      setSelectedAds([]);
      setFilledTags([]);
      queryClient.invalidateQueries();
    },
  });

  const updatePlanTags = (type = "add") => {
    for (let i = 0; i < selectedAds.length; i++) {
      const ad = data?.find((ad) => ad.id === selectedAds[i]);
      const updatedTags =
        type === "remove" ?
          ad?.tags?.filter((tag) => !filledTags?.includes(tag))
        : [...(ad?.tags ?? []), ...filledTags];

      updateTags.mutate({
        ad_id: ad.id,
        tags: [...new Set(updatedTags)],
        isLast: i === selectedAds?.length - 1,
      });
    }
  };

  const updatePlanCategories = async () => {
    for (let i = 0; i < selectedAds?.length; i++) {
      const adPlanData = stats.find((ad) => ad.ad_id === selectedAds[i]);

      const updateData = {
        id: selectedAds[i],
        state: adPlanData.state,
        custom_api_request: "update_advertisement",
        name: adPlanData.ad_name,
        category: parseInt(destinationCategoryId),
      };

      updateHandler.mutate({
        data: updateData,
        isLast: i === selectedAds?.length - 1,
      });
    }
  };

  const updatePlanBiddingProgram = async () => {
    for (let i = 0; i < selectedAds?.length; i++) {
      const adPlanData = stats.find((ad) => ad.ad_id === selectedAds[i]);

      const updateData = {
        id: selectedAds[i],
        state: adPlanData.state,
        custom_api_request: "update_advertisement",
        name: adPlanData.ad_name,
        ad_control_fields: { program: bulkActionFormData.biddingProgram },
      };

      updateHandler.mutate({
        data: updateData,
        isLast: i === selectedAds?.length - 1,
      });
    }
  };

  const updatePlanNTMEnrollment = async (enrollmentStatus) => {
    for (let i = 0; i < selectedAds?.length; i++) {
      const adPlanData = stats.find((ad) => ad.ad_id === selectedAds[i]);

      const updateData = {
        id: selectedAds[i],
        state: adPlanData.state,
        custom_api_request: "update_advertisement",
        name: adPlanData.ad_name,
      };

      // Import keywords and targets
      if (enrollmentStatus === "importKeywordsAndTargets") {
        updateData["non_trellis_enrolled_ad"] = true;
      }

      // Accept bid management
      if (enrollmentStatus === "manageBids") {
        updateData["non_trellis_enrolled_ad"] = true;
        updateData["non_trellis_enrolled_ad_accept_bids"] = true;
      }

      // Handle remove bid management
      if (enrollmentStatus === "revokeBidManagement") {
        updateData["non_trellis_enrolled_ad"] = true;
        updateData["non_trellis_enrolled_ad_accept_bids"] = false;
      }

      updateHandler.mutate({
        data: updateData,
        isLast: i === selectedAds?.length - 1,
        // Below is a flag to skip the request altogether if the ad isn't imported
        skip: adPlanData.ad_goal !== GOALS.IMPORTED_ADS.value,
      });
    }
  };

  const updatePlanGoals = async (changes = {}) => {
    const editData = stats
      ?.filter((row) =>
        Object.keys(changes)
          ?.map((id) => parseInt(id))
          ?.includes(row.id),
      )
      ?.map((row) => {
        return { ...row, ...changes[row.id] };
      });

    for (let i = 0; i < editData.length; i++) {
      const adPlanData = stats?.find((ad) => ad.ad_id === editData[i].id);
      let updatedStatus;

      const statusUpdate = editData[i].state !== adPlanData.state;
      const paramsUpdate =
        editData[i]?.max_acos !== adPlanData.max_acos ||
        editData[i]?.target_acos !== adPlanData.target_acos ||
        editData[i]?.daily_budget !== adPlanData.daily_budget;

      if (statusUpdate) {
        const updateStatusBodyData = {
          id: editData[i]?.id,
          state: editData[i]?.state,
          name: adPlanData.ad_name,
        };

        const updateStatusRes = await updateStatusHandler.mutateAsync({
          data: updateStatusBodyData,
          isLast: i === editData.length - 1 && !paramsUpdate,
        });

        updatedStatus = updateStatusRes.data.new_campaign_state;
      }

      if (paramsUpdate) {
        const updateData = {
          id: editData[i]?.id,
          state: statusUpdate ? updatedStatus : adPlanData.state,
          custom_api_request: "update_advertisement",
          name: adPlanData.ad_name,
          max_acos: editData[i]?.max_acos,
          target_acos: editData[i]?.target_acos,
          daily_budget: editData[i]?.daily_budget,
        };

        if (channel === "walmart") {
          const minBudgetRequirement = getWalmartBudgetMinimum(
            adPlanData.ad_type,
            adPlanData.ad_goal,
            user.organization_type,
            adPlanData.auto_campaign_enabled,
          );
          if (updateData["daily_budget"] < minBudgetRequirement) {
            errorToast({
              message: `Invalid budget provided for media plan ${updateData.name}. Must be at least ${formatCurrency(minBudgetRequirement, marketPlace.marketPlace, true)}`,
            });
            continue;
          }
        }

        await updateHandler.mutateAsync({
          data: updateData,
          isLast: i === editData.length - 1 && paramsUpdate,
        });
      }
    }
  };

  const updatePlanGoalsBulk = (target) => {
    const adsToBulkUpdate = selectedAds.map((adId) => stats?.find((ad) => ad.id === adId));

    for (let i = 0; i < adsToBulkUpdate.length; i++) {
      let { actionType, actionValue, actionUnit } = bulkActionFormData;
      const adData = adsToBulkUpdate[i];

      const { max_acos, target_acos } = adData;

      let originalACOSValue;
      let currentROAS;

      const updateData = {
        id: adData.id,
        state: adData.state,
        custom_api_request: "update_advertisement",
        name: adData.ad_name,
        max_acos: adData.max_acos,
        target_acos: adData.target_acos,
        daily_budget: adData?.daily_budget,
      };

      // Below should be revisited. The conversion got tricky within the loop due to the flipped around change directions with bulk actions
      // Ideally, we could add support for ROAS updates to the advertisement update view
      let resetActionTargetValue;

      if (target === "min_roas") {
        resetActionTargetValue = "min_roas";
        // Revise to max_acos as target, convert values
        target = "max_acos";
        if (actionType === "specify") {
          actionValue = (1 / parseFloat(actionValue)) * 100;
        }

        if (["increase", "decrease"]?.includes(actionType)) {
          originalACOSValue = adData.max_acos / 100;
          currentROAS = 1 / originalACOSValue;

          const newValue = (1 / (currentROAS + parseFloat(actionValue))) * 100;
          actionValue = max_acos - newValue;
          actionType = actionType === "decrease" ? "increase" : "decrease";
        }

        actionUnit = "percent";
      }

      if (target === "target_roas") {
        resetActionTargetValue = "target_roas";
        // Revise to max_acos as target, convert values
        target = "target_acos";

        if (actionType === "specify") {
          actionValue = (1 / parseFloat(actionValue)) * 100;
        }
        if (["increase", "decrease"]?.includes(actionType)) {
          originalACOSValue = adData.target_acos / 100;
          currentROAS = 1 / originalACOSValue;

          const newValue = (1 / (currentROAS + parseFloat(actionValue))) * 100;

          actionValue = target_acos - newValue;
          actionType = actionType === "decrease" ? "increase" : "decrease";
        }
        actionUnit = "percent";
      }

      updateData[target] = updateValues(
        adData[target],
        actionValue,
        actionUnit,
        actionType,
        target.includes("acos") ? "percent" : "currency",
      );

      if (resetActionTargetValue) {
        target = resetActionTargetValue;
      }
      // Validate walmart minimum budget
      if (channel === "walmart" && target === "daily_budget") {
        const minBudgetRequirement = getWalmartBudgetMinimum(
          adData.ad_type,
          adData.ad_goal,
          user.organization_type,
          adData.auto_campaign_enabled,
        );
        if (updateData["daily_budget"] < minBudgetRequirement) {
          errorToast({
            message: `Invalid budget provided for media plan ${updateData.name}. Must be at least ${formatCurrency(minBudgetRequirement, marketPlace.marketPlace, true)}`,
          });
          continue;
        }
      }

      if (target === "daily_budget") {
        updateData["daily_budget"] = Math.trunc(updateData["daily_budget"]);
      }

      updateHandler.mutate({
        data: updateData,
        isLast: i === selectedAds.length - 1,
      });
    }
  };

  const data = useMemo(() => {
    const data =
      stats?.map((p) => {
        p.target_roas = acosToRoas(p["target_acos"]);
        p.min_roas = acosToRoas(p["max_acos"]);

        return { ...p };
      }) ?? [];

    return data;
  }, [stats]);

  const customTags = useMemo(() => {
    return [...new Set(data?.map((ad) => ad.tags)?.flat())];
  }, [data]);

  const biddingPrograms = useMemo(() => {
    return [...new Set(data?.map((ad) => ad.bidding_program_name)?.flat())];
  }, [data]);

  const bulkActionOptions = [
    {
      label: (selected) => `Pause Media Plan${selected.length > 1 ? "s" : ""}`,
      action: (selected) => updatePlanState("paused", selected),
      confirmationMessage: <p>Are you sure you want to pause the selected media plans?</p>,
    },
    {
      label: (selected) => `Enable Media Plan${selected.length > 1 ? "s" : ""}`,
      action: (selected) => updatePlanState("active", selected),
      confirmationMessage: <p>Are you sure you want to enable the selected media plans?</p>,
    },
    {
      label: (selected) => `Pause Media Plan${selected?.length > 1 ? "s" : ""} (Bulk)`,
      action: (selected) => bulkUpdatePlanState("paused", selected),
      confirmationMessage: <p className="fs-4 py-2">Are you sure you want to pause the selected media plans?</p>,
      hidden: !hasBulkActionsModuleEnabled,
    },
    {
      label: (selected) => `Enable Media Plan${selected?.length > 1 ? "s" : ""} (Bulk)`,
      action: (selected) => bulkUpdatePlanState("active", selected),
      confirmationMessage: <p className="fs-4 py-2">Are you sure you want to enable the selected media plans?</p>,
      hidden: !hasBulkActionsModuleEnabled,
    },
    acosOrRoas(
      {
        label: "Update Optimal ACOS Range Low",
        action: () => updatePlanGoalsBulk("target_acos"),
        confirmationMessage: (
          <p className="fs-4 py-2">Are you sure you want to update the target ACOS for the selected media plans?</p>
        ),
        actionFormFields: (
          <BulkAdPlanActionForm
            formData={bulkActionFormData}
            setFormData={setBulkActionFormData}
            actionTarget={"Optimal ACOS Range Low"}
            hideCurrencyOption={true}
            forcePercentOption={true}
          />
        ),
      },
      {
        label: "Update Optimal ROAS Range High",
        action: () => updatePlanGoalsBulk("target_roas"),
        confirmationMessage: (
          <p className="fs-4 py-2">Are you sure you want to update the target ROAS for the selected media plans?</p>
        ),
        actionFormFields: (
          <BulkAdPlanActionForm
            formData={bulkActionFormData}
            setFormData={setBulkActionFormData}
            actionTarget={"Optimal ROAS Range High"}
            hideCurrencyOption={true}
            hidePercentOption={true}
          />
        ),
      },
    ),
    acosOrRoas(
      {
        label: "Update Optimal ACOS Range High",
        action: () => updatePlanGoalsBulk("max_acos"),
        confirmationMessage: (
          <p className="fs-4 py-2">Are you sure you want to update the maximum ACOS for the selected media plans?</p>
        ),
        actionFormFields: (
          <BulkAdPlanActionForm
            formData={bulkActionFormData}
            setFormData={setBulkActionFormData}
            actionTarget={"Optimal ACOS Range High"}
            hideCurrencyOption={true}
            forcePercentOption={true}
          />
        ),
      },
      {
        label: "Update Optimal ROAS Range Low",
        action: () => updatePlanGoalsBulk("min_roas"),
        confirmationMessage: (
          <p className="fs-4 py-2">Are you sure you want to update the maximum ROAS for the selected media plans?</p>
        ),
        actionFormFields: (
          <BulkAdPlanActionForm
            formData={bulkActionFormData}
            setFormData={setBulkActionFormData}
            actionTarget={"Optimal ROAS Range Low"}
            hideCurrencyOption={true}
            hidePercentOption={true}
          />
        ),
      },
    ),
    {
      label: "Update Budget",
      action: () => updatePlanGoalsBulk("daily_budget"),
      confirmationMessage: (
        <p className="fs-4 py-2">Are you sure you want to update the daily budget for the selected media plans?</p>
      ),
      actionFormFields: (
        <BulkAdPlanActionForm
          formData={bulkActionFormData}
          setFormData={setBulkActionFormData}
          actionTarget={"Daily Budget"}
          hidePercentOption={bulkActionFormData?.actionType === "specify"}
        />
      ),
    },
    {
      label: "Schedule Budget Updates",
      action: () => scheduleBudgetUpdates(),
      confirmationMessage: (
        <p className="fs-4 py-2">
          {(
            !parseFloat(bulkActionFormData?.actionValue) ||
            !bulkActionFormData?.actionType ||
            !bulkActionFormData?.actionUnit ||
            !bulkActionFormData?.startDate ||
            !bulkActionFormData?.endDate
          ) ?
            "Please fill out all required fields."
          : `Are you sure you want to ${bulkActionFormData?.actionType} the daily budget by ${
              bulkActionFormData?.actionUnit === "percent" ?
                `${Math.round(Math.abs(bulkActionFormData?.actionValue))}%`
              : formatCurrency(Math.abs(bulkActionFormData?.actionValue), marketPlace)
            } for the selected media plans from ${moment(bulkActionFormData?.startDate).format("MMMM D")} to ${moment(
              bulkActionFormData?.endDate,
            ).format("MMMM D")}  (${
              (moment(bulkActionFormData?.endDate).diff(bulkActionFormData?.startDate, "days") + 1) * 24
            } hours)?`
          }
        </p>
      ),

      actionFormFields: (
        <BulkAdPlanActionForm
          formData={bulkActionFormData}
          setFormData={setBulkActionFormData}
          actionTarget={"Daily Budget"}
          schedulingDialogue={true}
        />
      ),
      disableConfirmButton:
        !parseFloat(bulkActionFormData?.actionValue) ||
        !bulkActionFormData?.actionType ||
        !bulkActionFormData?.actionUnit ||
        !bulkActionFormData?.startDate ||
        !bulkActionFormData?.endDate,
    },
    {
      label: `Cancel Scheduled Budget Updates`,
      action: () => {
        bulkUpdatePropertiesMutation.mutate({
          advertisements: selectedAds,
          updates: [
            {
              field: "cancel_scheduled_actions",
              action: "set",
              value: "cancelled",
            },
          ],
        });
      },
      confirmationMessage: (
        <p className="fs-4 py-2">
          Are you sure you want to cancel scheduled budget updates for the selected media plan
          {selectedAds?.length > 1 ? "s" : ""}?
        </p>
      ),
    },
    {
      label: `Update Plan ${selectedAds?.length > 1 ? "Categories" : "Category"}`,
      action: () => updatePlanCategories(),
      confirmationMessage: (
        <p className="fs-4 py-2">
          Are you sure you want to update the category for the{" "}
          {selectedAds?.length > 1 ? `${selectedAds?.length} selected media plans` : "selected media plan"}?
        </p>
      ),
      actionFormFields: (
        <div className="pb-5">
          <p className="pb-4 fs-4">
            Please specify the destination category you would like to move the selected media plan
            {selectedAds?.length > 1 ? "s" : ""} to.
          </p>
          <div className="d-flex justify-content-center">
            <CategoryField
              categories={categories}
              dashboard={true}
              onChange={(value) => {
                setDestinationCategoryId(value.categoryId);
              }}
              state={{
                updatedCategoryId: destinationCategoryId ? destinationCategoryId : categoryDetails?.id,
              }}
              errors={{}}
              channel={channel}
              marketPlace={marketPlace}
              existingAdCategorySelector={true}
            />
          </div>
        </div>
      ),
    },
    {
      label: "Add Media Plan Tags",
      action: () => {
        updatePlanTags();
      },
      actionFormFields: (
        <BulkTagsForm
          filledTags={filledTags}
          setFilledTags={setFilledTags}
          selected={selectedAds}
          suggestedTags={customTags}
          taggedItemDescription={"media plan"}
          actionType={"add"}
        />
      ),
      confirmationMessage: (
        <p>
          Are you sure you want to add {filledTags?.length > 1 ? "these tags" : "this tag"} from the selected media plan
          {selectedAds?.length > 1 ? "s" : ""}?
        </p>
      ),
      disableConfirmButton: !filledTags.length,
    },
    {
      label: "Remove Media Plan Tags",
      action: () => {
        updatePlanTags("remove");
      },
      actionFormFields: (
        <BulkTagsForm
          filledTags={filledTags}
          setFilledTags={setFilledTags}
          selected={selectedAds}
          suggestedTags={customTags}
          taggedItemDescription={"media plan"}
          actionType={"remove"}
        />
      ),
      confirmationMessage: (
        <p>
          Are you sure you want to remove {filledTags?.length > 1 ? "these tags" : "this tag"} from the selected media
          plan
          {selectedAds?.length > 1 ? "s" : ""}?
        </p>
      ),
      disableConfirmButton: !filledTags.length,
    },
    {
      label: "Update Dayparting",
      action: () => {
        updateDayParting.mutate({
          day_parting_method: dayPartingData.day_parting_method,
          day_parting_multipliers: dayPartingData.day_parting_multipliers,
          advertisement_ids: selectedAds,
        });
      },
      actionFormFields: (
        <BulkDayPartingChart
          advertisements={selectedAds}
          onDayPartingChange={setDayPartingData}
          categoryId={categoryDetails?.id}
        />
      ),
      confirmationMessage: (
        <p>
          Are you sure you want to update the dayparting for the selected media plan
          {selectedAds?.length > 1 ? "s" : ""}?
        </p>
      ),
    },
    {
      label: "Update Bidding Rules",
      action: () => {
        updatePlanBiddingProgram();
      },
      actionFormFields: <BulkAdPlanRulesForm formData={bulkActionFormData} setFormData={setBulkActionFormData} />,
      confirmationMessage: (
        <p>
          {bulkActionFormData.biddingProgram === undefined ?
            ""
          : `Are you sure you want to update the bidding rules for the selected media plan${selectedAds?.length > 1 ? "s" : ""}?`
          }
        </p>
      ),
      disableConfirmButton: bulkActionFormData.biddingProgram === undefined,
    },
  ];

  if (hasNTMModuleEnabled) {
    // Import NTM keywords and targets
    bulkActionOptions.push({
      label: "Import Keywords and Targets",
      action: () => {
        updatePlanNTMEnrollment("importKeywordsAndTargets");
      },
      confirmationMessage: (
        <p className={css({ textAlign: "center" })}>
          Are you sure you want to import targets and keywords for the selected media plan
          {selectedAds?.length > 1 ? "s" : ""}? Once they are imported you can enroll this campaign in Trellis to have
          it automatically managed by our AI.
        </p>
      ),
    });

    // Accept NTM bid management
    bulkActionOptions.push({
      label: "Manage Imported Media Plans",
      action: () => {
        updatePlanNTMEnrollment("manageBids");
      },
      confirmationMessage: (
        <p>
          Are you sure you want to enroll and automatically accept bids for the selected media plan
          {selectedAds?.length > 1 ? "s" : ""}?
        </p>
      ),
    });

    // Revoke NTM bid management
    bulkActionOptions.push({
      label: "Unmanage Imported Media Plans",
      action: () => {
        updatePlanNTMEnrollment("revokeBidManagement");
      },
      confirmationMessage: (
        <p>
          Are you sure you want to disable bid management for the selected media plan
          {selectedAds?.length > 1 ? "s" : ""}?
        </p>
      ),
    });
  }

  const ADDITIONAL_FILTERS = [
    AD_STATUS_FILTER,
    AD_SOURCE_FILTER,
    NTM_STATUS_FILTER,
    SCHEDULED_BUDGET_CHANGES_FILTER,
    buildCustomTagsFilter(customTags),
    buildBiddingProgramFilter(biddingPrograms),
    AD_NAME_FILTER,
    SERVING_STATUS_FILTER,
  ];

  const INITIAL_FILTERS = [{ ...AD_STATUS_FILTER, value: [1] }];

  const filterProps = useCustomTableFilters(TABLE_ID);

  if (isLoading) {
    return <Loading height={"300px"} fullPage={false} />;
  }
  const disableSelectionList = data.filter((ad) => ad.serving_status === "ENDED").map((ad) => ad.ad_id);

  return (
    <>
      <BulkActionFeedbackModal
        showBulkActionFeedbackModal={showBulkActionFeedbackModal}
        bulkActionFeedback={bulkActionFeedback}
        clearBulkActionFeedback={() => {
          setShowBulkActionFeedbackModal(false);
          setBulkActionFeedback(null);
        }}
        setSelected={setSelectedAds}
      />
      {showTabs && (
        <Flex justify={"space-between"} wrap={"wrap"} gap="small" alignContent={"center"} paddingBottom={"medium"}>
          <ButtonGroup
            buttons={[
              { label: "All Media Plans", value: "all", counter: adGroups?.all?.length },
              {
                label: "New",
                value: "recent",
                counter: adGroups?.recent?.length,
              },
              { label: "Trending Up", value: "trend_up", counter: adGroups?.trend_up?.length },
              {
                label: "Trending Down",
                value: "trend_down",
                counter: adGroups?.trend_down?.length,
              },
              {
                label: acosOrRoas("High ACOS", "Low ROAS"),
                value: "high_acos",
                counter: adGroups?.high_acos?.length,
              },
              {
                label: acosOrRoas("Low ACOS", "High ROAS"),
                value: "low_acos",
                counter: adGroups?.low_acos?.length,
              },
              {
                label: acosOrRoas("ACOS in Range", "ROAS in Range"),
                value: "in_range_acos",
                counter: adGroups?.in_range_acos?.length,
              },
              { label: "Low Budget", value: "serving_low", counter: adGroups?.serving_low?.length },
            ]}
            value={tab}
            onSelect={setTab}
          />
        </Flex>
      )}

      <BulkActionsTable
        tableRef={tableRef}
        tableId={TABLE_ID}
        entityType={"mediaPlan"}
        disableSelectionList={disableSelectionList}
        titleCol={{
          id: "advertisement",
          isStatic: true,
          Header: "Advertisement",
          accessor: "name",
          style: { width: smallViewportWidthTable ? "250px" : "350px" },
          width: smallViewportWidthTable ? 250 : 350,
          Cell: ({ row }) => {
            return (
              <AdvertisementCell
                ad={{
                  ...row._original,
                  id: row._original?.product_id,
                }}
                seasonalCategory={categoryDetails?.seasonalCategory}
              />
            );
          },
        }}
        columnSpecs={[
          {
            key: "state",
            compareKey: null,

            options: {
              editCell: true,
              hideFilter: false,
              filterType: "select",
              selectOptions: [
                {
                  value: 1,
                  label: "Active",
                },
                {
                  value: 0,
                  label: "Inactive",
                },
              ],
              format: "toggle",
              onValue: 1,
              offValue: 0,
              width: 150,
              className: "d-flex justify-content-end",
              columnTitle: "Plan Status",
              renderPlaceholderCellContent: (row) => {
                return row._original.is_composite || row._original.serving_status === "ENDED";
              },
            },
          },
          {
            key: "serving_status",
            compareKey: null,
            options: {
              columnTitle: "Serving Status",
              className: "text-end",
              formatter: (v) => {
                return servingStatusOptions.find((option) => option.value === v)?.label;
              },
              checked: channel !== "walmart",
            },
          },
          {
            key: "daily_budget",
            compareKey: null,
            options: {
              editCell: true,
              minValue: 1,
              columnTitle: "Daily Budget",
              className: "text-end",
            },
          },
          acosOrRoas(
            {
              key: "target_acos",
              compareKey: null,
              options: {
                editCell: true,
                minValue: 0,
                columnTitle: "ACOS Range Low",
                className: "text-end",
                format: "percent",
                renderPlaceholderCellContent: (row) => {
                  const nonManaged = row._original?.ad_goal === GOALS.IMPORTED_ADS.value;
                  const enrolled = row._original?.non_trellis_enrolled_ad;
                  // Dont show data for this field if ad is non-managed and not enrolled
                  if (nonManaged && !enrolled) {
                    return true;
                  }
                  return false;
                },
              },
            },
            {
              key: "target_roas",
              compareKey: null,
              options: {
                editCell: true,
                minValue: 0,
                columnTitle: "ROAS Range High",
                className: "text-end",
                format: "number",
                renderPlaceholderCellContent: (row) => {
                  const nonManaged = row._original?.ad_goal === GOALS.IMPORTED_ADS.value;
                  const enrolled = row._original?.non_trellis_enrolled_ad;
                  // Dont show data for this field if ad is non-managed and not enrolled
                  if (nonManaged && !enrolled) {
                    return true;
                  }
                  return false;
                },
              },
            },
          ),
          acosOrRoas(
            {
              key: "max_acos",
              compareKey: null,
              options: {
                editCell: true,
                minValue: 0,
                columnTitle: "ACOS Range High",
                className: "text-end",
                format: "percent",
                renderPlaceholderCellContent: (row) => {
                  const nonManaged = row._original?.ad_goal === GOALS.IMPORTED_ADS.value;
                  const enrolled = row._original?.non_trellis_enrolled_ad;
                  // Dont show data for this field if ad is non-managed and not enrolled
                  if (nonManaged && !enrolled) {
                    return true;
                  }
                  return false;
                },
              },
            },
            {
              key: "min_roas",
              compareKey: null,
              options: {
                editCell: true,
                minValue: 0,
                columnTitle: "ROAS Range Low",
                className: "text-end",
                format: "number",
                renderPlaceholderCellContent: (row) => {
                  const nonManaged = row._original?.ad_goal === GOALS.IMPORTED_ADS.value;
                  const enrolled = row._original?.non_trellis_enrolled_ad;
                  // Dont show data for this field if ad is non-managed and not enrolled
                  if (nonManaged && !enrolled) {
                    return true;
                  }
                  return false;
                },
              },
            },
          ),
          { key: "ad_sales", compareKey: "ad_sales_percent" },
          { key: "ad_spend", compareKey: "ad_spend_percent" },
          {
            key: "cost",
            compareKey: "cost_percent",
            options: {
              noColor: true,
              checked: false,
            },
          },
          {
            key: "roas",
            compareKey: "roas_delta",
            options: {
              checked: acosOrRoas(false, true),
            },
          },
          {
            key: "acos",
            compareKey: "acos_delta",
            options: {
              checked: acosOrRoas(true, false),
              isNegative: true,
            },
          },
          { key: "clicks", compareKey: "clicks_percent" },
          { key: "impressions", compareKey: "impressions_percent" },
          {
            key: "ctr",
            compareKey: "ctr_delta",
            options: {
              checked: false,
              decimalPlaces: 2,
            },
          },
          {
            key: "orders",
            compareKey: "orders_percent",
            options: {
              checked: false,
            },
          },
          {
            key: "conversion_rate",
            compareKey: "conversion_rate_delta",
            options: {
              checked: false,
              decimalPlaces: 1,
            },
          },
          {
            key: "cpc",
            compareKey: "cpc_delta",
            options: {
              checked: false,
              noColor: true,
              clipDecimalPlaces: false,
            },
          },
          {
            key: "serving_hours",
            compareKey: "serving_hours_delta",
            options: {
              checked: false,
            },
          },
          buildDateColumn("created_datetime", "Created On"),
        ]}
        minRows={0}
        defaultPageSize={defaultPageSize}
        data={data}
        selected={selectedAds}
        setSelected={setSelectedAds}
        selectionKey="ad_id"
        bulkActionOptions={bulkActionOptions}
        getSearchCriteria={(row) => {
          const { name, ad_name, id, ad_type, tags, bidding_program_name } = row;
          const adType = getAdTypeName(ad_type);
          return `${name} ${ad_name} ${id} ${adType} ${tags?.join(" ")} ${bidding_program_name}`;
        }}
        leftActionButton={leftActionButton}
        additionalFilterOptions={ADDITIONAL_FILTERS}
        excludedFilterColumns={["state", "serving_status"]}
        defaultSorted={[{ id: "ad_sales", desc: true }]}
        isLoading={isLoading || updateStatusHandler.isPending || updateHandler.isPending}
        isSelectable={true}
        isExpandable={true}
        filterWidget={true}
        hideFilterToggleIcon={true}
        hideResetFiltersIcon={false}
        {...filterProps}
        initialFilters={INITIAL_FILTERS}
        handleBulkChanges={(changes) => updatePlanGoals(changes)}
        ExpandedContent={({ row }) => {
          return (
            <div className={css({ backgroundColor: "lightgray" })}>
              <ExpanderContainer>
                <AdvertisementExpander adId={row.original.id} />
              </ExpanderContainer>
            </div>
          );
        }}
      />
    </>
  );
};

export default AdvertisementsTable;
